// src/hooks/usePayment.ts
// INFINI — usePayment Hook (GP1 API)
//
// Contract expected by GP1 App.tsx:
//   const { initiateCheckout, isLoading, error } = usePayment({ onSuccess, onError })
//
// Internally: POSTs to the Supabase create-payment-intent Edge Function,
// then redirects to Stripe. On Stripe return, App.tsx polls for seat assignment.

import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface UsePaymentOptions {
  onSuccess: (sessionId: string) => void
  onError: (message: string) => void
}

export interface InitiateCheckoutParams {
  tier: string
  userId?: string | null
  userEmail?: string
}

export interface UsePaymentReturn {
  /** Call this on the Pay button click */
  initiateCheckout: (params: InitiateCheckoutParams) => Promise<void>
  isLoading: boolean
  error: string | null
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function usePayment({ onSuccess, onError }: UsePaymentOptions): UsePaymentReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const initiateCheckout = useCallback(
    async ({ tier, userId, userEmail }: InitiateCheckoutParams) => {
      setIsLoading(true)
      setError(null)

      try {
        // ── 1. Get the current session token ──────────────────────────────
        const {
          data: { session },
        } = await supabase.auth.getSession()
        const token = session?.access_token

        if (!token) {
          throw new Error('No active session — please refresh and try again.')
        }

        // ── 2. Call Supabase Edge Function to create Stripe Checkout session
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-payment-intent`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              tier,
              userId,
              userEmail,
              successUrl: `${window.location.origin}/?payment=success`,
              cancelUrl:  `${window.location.origin}/?payment=cancel`,
            }),
          }
        )

        const json = await res.json()

        if (!res.ok) {
          throw new Error(
            (json as { error?: string }).error ??
              `Server error (HTTP ${res.status})`
          )
        }

        const payload = json as {
          checkoutUrl?: string
          sessionId?: string
        }

        // ── 3. Stripe redirect ────────────────────────────────────────────
        if (payload.checkoutUrl) {
          onSuccess(payload.sessionId ?? '')
          window.location.href = payload.checkoutUrl
          return
        }

        if (payload.sessionId) {
          onSuccess(payload.sessionId)
          const stripeModule = await import('@stripe/stripe-js')
          const stripe = await stripeModule.loadStripe(
            import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string
          )
          if (!stripe) throw new Error('Stripe failed to load.')
          const { error: stripeError } = await stripe.redirectToCheckout({
            sessionId: payload.sessionId,
          })
          if (stripeError) throw new Error(stripeError.message)
          return
        }

        throw new Error('Unexpected response from payment server.')
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : 'Payment initialisation failed. Please try again.'
        setError(msg)
        onError(msg)
      } finally {
        setIsLoading(false)
      }
    },
    [onSuccess, onError]
  )

  return { initiateCheckout, isLoading, error }
}
